import { useContext, useEffect, useMemo, useState } from 'react';
import { LineItemType } from '@lambda/apis/src/cart/types';
import { DeliveryEstimateT, OfferT } from '@lambda/apis/src/catalog-v2/types';
import { AppCtx } from 'pages/_app';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';

export default function useDeliveryEstimates(): Record<
  string,
  DeliveryEstimateT[]
> {
  const { cartLines } = useCartContext();
  const { zipcodeStateData } = useContext(AppCtx);
  const [fetched, setFetched] = useState<{
    destination: string;
    byLine: Record<string, DeliveryEstimateT[]>;
  } | null>(null);

  const productLines = useMemo(
    () => cartLines.filter((line) => line.type === LineItemType.PRODUCT),
    [cartLines],
  );

  const offerIds = useMemo(
    () => productLines.map((line) => line.typeId).filter(Boolean),
    [productLines],
  );

  const offerIdsKey = offerIds.join(',');
  const destinationKey = `${zipcodeStateData?.state ?? ''}|${
    zipcodeStateData?.region ?? ''
  }`;

  useEffect(() => {
    if (offerIds.length === 0) {
      setFetched({ destination: destinationKey, byLine: {} });

      return undefined;
    }

    const controller = new AbortController();

    const fetchEstimates = async () => {
      try {
        const res = await fetch(
          `/api/catalog-v2/offers?offerIds=${encodeURIComponent(offerIdsKey)}`,
          { signal: controller.signal },
        );

        if (!res.ok) {
          return;
        }

        const { items }: { items: OfferT[] } = await res.json();
        const offersById = new Map(items.map((offer) => [offer.id, offer]));

        const result = productLines.reduce<Record<string, DeliveryEstimateT[]>>(
          (acc, line) => {
            const deliveryEstimates = offersById.get(
              line.typeId,
            )?.deliveryEstimates;

            if (deliveryEstimates?.length) {
              acc[line.id] = deliveryEstimates;
            }

            return acc;
          },
          {},
        );

        setFetched({ destination: destinationKey, byLine: result });
      } catch {
        // Request aborted (cart changed/unmounted) or failed — keep the cart
        // usable; DeliveryToggle falls back to the note-based dates.
      }
    };

    fetchEstimates();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerIdsKey, destinationKey]);

  return fetched?.destination === destinationKey ? fetched.byLine : {};
}
